import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Grid,
  Tooltip,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Checkbox,
} from "@mui/material";
import {
  SaveEnviroTestKit,
  UpdateEnviroTestKit,
} from "../../../services/EnviroTestKitsService";
import { NotificationManager } from "react-notifications";
import MultiSelectSlates from "../../common/MultiSelectSlates";
import MultiSelectTestLib from "../../common/MultiSelectTestLib";

const AddEnviroTestKit = ({ open, onClose, onSave, selectedKit }) => {
  const [numberOfSamples, setNumberOfSamples] = useState("");
  const [nameFromSite, setNameFromSite] = useState("");
  const [vlpName, setVlpName] = useState("");
  const [shortLabelName, setShortLabelName] = useState("");
  const [sampleLocation, setSampleLocation] = useState("");
  const [priority, setPriority] = useState("");
  const [turnAroundTime, setTurnAroundTime] = useState("");
  const [sampleSlateJson, setSampleSlateJson] = useState("");
  const [otherTestsToAddJson, setOtherTestsToAddJson] = useState("");
  const [typeFormLink, setTypeFormLink] = useState("");
  const [typeFormLinkPostPay, setTypeFormLinkPostPay] = useState("");
  const [showInOrderGenerator, setShowInOrderGenerator] = useState(true);

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (selectedKit) {
      setNumberOfSamples(selectedKit.numberOfSamples || "");
      setNameFromSite(selectedKit.nameFromSite || "");
      setVlpName(selectedKit.vlpName || "");
      setShortLabelName(selectedKit.shortLabelName || "");
      setSampleLocation(selectedKit.sampleLocation || "");
      setPriority(selectedKit.priority || "");
      setTurnAroundTime(selectedKit.turnAroundTime || "");
      setSampleSlateJson(selectedKit.sampleSlateJson || "");
      setOtherTestsToAddJson(selectedKit.otherTestsToAddJson || "");
      setTypeFormLink(selectedKit.typeFormLink || "");
      setTypeFormLinkPostPay(selectedKit.typeFormLinkPostPay || "");
      // Legacy kits saved before this flag existed come back null - treat as visible
      setShowInOrderGenerator(selectedKit.showInOrderGenerator !== false);
    } else {
      resetForm();
    }
  }, [selectedKit]);

  const resetForm = () => {
    setNumberOfSamples("");
    setNameFromSite("");
    setVlpName("");
    setShortLabelName("");
    setSampleLocation("");
    setPriority("");
    setTurnAroundTime("");
    setSampleSlateJson("");
    setOtherTestsToAddJson("");
    setTypeFormLink("");
    setTypeFormLinkPostPay("");
    setShowInOrderGenerator(true);
  };

  const validate = () => {
    const newErrors = {};

    // Required field checks
    if (!numberOfSamples)
      newErrors.numberOfSamples = "Number of Samples is required";
    if (!nameFromSite) newErrors.nameFromSite = "Kit Type is required";
    if (!vlpName) newErrors.vlpName = "TypeForm Web Name is required";
    if (!shortLabelName) newErrors.shortLabelName = "Short Label Name is required";
    if (!priority) newErrors.priority = "Priority is required";
    if (!turnAroundTime)
      newErrors.turnAroundTime = "Turn Around Time is required";

    // Short Label Name validation (max 4 characters)
    if (shortLabelName && shortLabelName.length > 4) {
      newErrors.shortLabelName = "Short Label Name must be maximum 4 characters";
    }

    // TypeForm link validation
    if (typeFormLink && !typeFormLink.match(/^https?:\/\/.+/)) {
      newErrors.typeFormLink =
        "TypeForm link must be a valid HTTP or HTTPS URL";
    }

    if (typeFormLinkPostPay && !typeFormLinkPostPay.match(/^https?:\/\/.+/)) {
      newErrors.typeFormLinkPostPay =
        "TypeForm link must be a valid HTTP or HTTPS URL";
    }

    // Check if number of comma-separated values matches numberOfSamples
    const sampleSlateCount = sampleSlateJson
      ? sampleSlateJson.split(",").filter(Boolean).length
      : 0;
    const otherTestsCount = otherTestsToAddJson
      ? otherTestsToAddJson.split(",").filter(Boolean).length
      : 0;

    if (
      sampleSlateCount + otherTestsCount !==
      parseInt(numberOfSamples, 10)
      // &&
      // otherTestsCount !== parseInt(numberOfSamples, 10)
    ) {
      newErrors.sampleSlateJson = `Number of entries must match Number of Samples (${numberOfSamples})`;
      newErrors.otherTestsToAddJson = `Number of entries must match Number of Samples (${numberOfSamples})`;
    }

    // If both fields are empty
    if (!sampleSlateJson && !otherTestsToAddJson) {
      newErrors.sampleSlateJson = "Sample Slate or Other Tests is required";
      newErrors.otherTestsToAddJson = "Sample Slate or Other Tests is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    try {
      const kitData = {
        sampleKitId: selectedKit?.sampleKitId || 0,
        sampleKitName: selectedKit?.sampleKitName || "",
        slateId: (selectedKit?.slateId || 0).toString(),
        isDeleted: selectedKit?.isDeleted || false,
        numberOfSamples: parseInt(numberOfSamples, 10),
        nameFromSite,
        vlpName,
        shortLabelName,
        sampleLocation,
        priority,
        turnAroundTime: turnAroundTime.toString(),
        sampleSlateJson,
        otherTestsToAddJson,
        typeFormLink,
        typeFormLinkPostPay,
        showInOrderGenerator,
      };

      let response;
      if (selectedKit) {
        response = await UpdateEnviroTestKit(kitData);
        NotificationManager.success("Enviro Test Kit updated successfully");
      } else {
        response = await SaveEnviroTestKit(kitData);
        NotificationManager.success("Enviro Test Kit saved successfully");
      }

      if (response?.status === 200) {
        onSave();
        onClose();
      } else {
        NotificationManager.error("Error saving Enviro Test Kit");
      }
    } catch (error) {
      NotificationManager.error("Error saving Enviro Test Kit");
    }
  };

  const handleCancel = () => onClose();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {selectedKit ? "Update Enviro Test Kit" : "Add New Enviro Test Kit"}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Number of Samples"
              type="number"
              value={numberOfSamples}
              onChange={(e) => setNumberOfSamples(e.target.value)}
              fullWidth
              margin="dense"
              variant="outlined"
              size="small"
              error={!!errors.numberOfSamples}
              helperText={errors.numberOfSamples}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Kit Type (internal use)"
              value={nameFromSite}
              onChange={(e) => setNameFromSite(e.target.value)}
              fullWidth
              margin="dense"
              variant="outlined"
              size="small"
              error={!!errors.nameFromSite}
              helperText={errors.nameFromSite}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="TypeForm Web Name"
              value={vlpName}
              onChange={(e) => setVlpName(e.target.value)}
              fullWidth
              margin="dense"
              variant="outlined"
              size="small"
              error={!!errors.vlpName}
              helperText={errors.vlpName}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Short Label Name"
              value={shortLabelName}
              onChange={(e) => setShortLabelName(e.target.value)}
              fullWidth
              margin="dense"
              variant="outlined"
              size="small"
              inputProps={{ maxLength: 4 }}
              error={!!errors.shortLabelName}
              helperText={errors.shortLabelName || "Maximum 4 characters"}
              placeholder="e.g., ENVR"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Sample Location"
              value={sampleLocation}
              onChange={(e) => setSampleLocation(e.target.value)}
              fullWidth
              margin="dense"
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl
              fullWidth
              margin="dense"
              variant="outlined"
              size="small"
              error={!!errors.priority}
            >
              <TextField
                select
                label="Priority"
                value={priority}
                size="small"
                onChange={(e) => setPriority(e.target.value)}
                SelectProps={{
                  native: true,
                }}
                helperText={errors.priority}
              >
                <option value="">Select Priority</option>
                <option value="High">High</option>
                <option value="Regular">Regular</option>
                <option value="Low">Low</option>
              </TextField>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label="Turn Around Time"
              type="number"
              value={turnAroundTime}
              onChange={(e) => setTurnAroundTime(e.target.value)}
              fullWidth
              margin="dense"
              variant="outlined"
              size="small"
              error={!!errors.turnAroundTime}
              helperText={errors.turnAroundTime}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showInOrderGenerator}
                  onChange={(e) => setShowInOrderGenerator(e.target.checked)}
                  size="small"
                />
              }
              label="Show in order generator kit list"
            />
            <FormHelperText>
              Uncheck to hide this kit from the New Order / bulk order kit
              dropdown. Existing orders and kit registrations are not affected.
            </FormHelperText>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="TypeForm Pre Pay Registration Link"
              value={typeFormLink}
              onChange={(e) => setTypeFormLink(e.target.value)}
              fullWidth
              margin="dense"
              variant="outlined"
              size="small"
              placeholder="https://example.typeform.com/to/your-form"
              error={!!errors.typeFormLink}
              helperText={
                errors.typeFormLink ||
                "Enter HTTPS link for kit registration form"
              }
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="TypeForm Post Pay Registration Link"
              value={typeFormLinkPostPay}
              onChange={(e) => setTypeFormLinkPostPay(e.target.value)}
              fullWidth
              margin="dense"
              variant="outlined"
              size="small"
              placeholder="https://example.typeform.com/to/your-form"
              error={!!errors.typeFormLinkPostPay}
              helperText={
                errors.typeFormLinkPostPay ||
                "Enter HTTPS link for kit registration form"
              }
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth error={!!errors.sampleSlateJson}>
              <MultiSelectSlates
                value={sampleSlateJson}
                onChange={(value) => setSampleSlateJson(value)}
              />
              <FormHelperText>{errors.sampleSlateJson}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth error={!!errors.otherTestsToAddJson}>
              <MultiSelectTestLib
                value={otherTestsToAddJson}
                onChange={(value) => setOtherTestsToAddJson(value)}
              />
              <FormHelperText>{errors.otherTestsToAddJson}</FormHelperText>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="secondary">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          {selectedKit ? "Update" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEnviroTestKit;